import { FC, useState, useEffect, useMemo, useCallback } from "react";

// import { useResourceState } from '../../contexts/kitchencontext'
import NewSearch from "../../molecules/NewSearch";
import MembersList, { MemberSortField } from '../../components/MembersList'
import ResourceRequestList, {
  ResourceRequestSortField,
} from '../../components/ResourceRequestList'
import { SortDirection } from '../../components/SortableHeader'
import { MembershipSchema, ResourceBookingSchema, ResourceType } from "../../data/types";
import { capitalizeString, filterMemberships, getDefaultMemberSortDirection, sortMemberships, } from "../../utils/helpers";
import FundingReferralInbox from "../../components/FundingReferralInbox";
import { FundingPipelineCampaign } from "../../components/FundingPipelineCampaign";


const filterResourceRequests = (
  requests: ResourceBookingSchema[],
  searchTerm: string
): ResourceBookingSchema[] => {
  const term = searchTerm.trim().toLowerCase()
  if (!term) return requests

  return requests.filter((request) =>
    request.user_full_name?.toLowerCase().includes(term) ||
    request.username?.toLowerCase().includes(term) ||
    request.resource_name?.toLowerCase().includes(term)
  )
}

const sortResourceRequests = (
  requests: ResourceBookingSchema[],
  field: ResourceRequestSortField,
  direction: SortDirection
): ResourceBookingSchema[] => {
  const sorted = [...requests]
  const multiplier = direction === 'asc' ? 1 : -1

  sorted.sort((a, b) => {
    let comparison = 0

    switch (field) {
      case 'user_full_name':
      case 'username':
      case 'resource_name':
      case 'status':
        comparison = (a[field] ?? '').localeCompare(b[field] ?? '', undefined, { sensitivity: 'base' })
        break
      case 'start_time':
      case 'created':
        comparison = new Date(a[field] ?? 0).getTime() - new Date(b[field] ?? 0).getTime()
        break
    }

    return comparison * multiplier
  })

  return sorted
}

export const AllMemberRequests: React.FC<{membershipRequests: MembershipSchema[]}> = ({membershipRequests}) => {
  const [searchTerm, changeSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<MemberSortField>('joined')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const kitchen_capacity =  1
  const itemsPerPage = 10

  const allMembers = membershipRequests ?? []

  const filteredMembers = useMemo(
    () => filterMemberships(membershipRequests ?? [], searchTerm),
    [membershipRequests, searchTerm]
  )

  const displayMembers = useMemo(
    () => sortMemberships(filteredMembers, sortField, sortDirection),
    [filteredMembers, sortField, sortDirection]
  )

  const totalPages = Math.ceil(displayMembers.length / itemsPerPage)
  const startIndex = displayMembers.length > 0 ? (currentPage - 1) * itemsPerPage : 0
  const endIndex = Math.min(startIndex + itemsPerPage, displayMembers.length)
  const currentMembers = displayMembers.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleSort = useCallback((field: MemberSortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSortField(field)
    setSortDirection(getDefaultMemberSortDirection(field))
  }, [sortField])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, sortField, sortDirection])

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  useEffect(() => {
    document.title = "HomeCook | Requests";
  }, [membershipRequests]);

  const hasSearchFilter = searchTerm.trim().length > 0

  return (
    <div className="w-full min-w-0 px-6">
    <div data-testid="member-requests-table-card" className="flex flex-col w-full min-w-0 bg-white my-6 rounded-xl min-h-[777px] shadow-md">
      <div className="flex flex-row w-full min-w-0 justify-between items-center gap-4 px-6 pt-6">
        <p className=" font-raleway text-[#8A8A8A] text-[14px] shrink-0" >
          {allMembers.length > 0 ? (
            displayMembers.length > 0
              ? `Showing ${startIndex + 1} - ${endIndex} of ${displayMembers.length} Member Requests`
              : "Showing 0 - 0 of 0 Member Requests"
          ) : (
            "Showing 0 - 0 of 0 Member Requests"
          )}
        </p>

        <div className="flex ml-auto w-full max-w-xs min-w-0">
          <NewSearch searchType="member name" searchTerm={searchTerm} changeSearchTerm={changeSearchTerm} />
        </div>
      </div>

      <div className="w-full min-w-0 px-6 pb-6">
        {
          allMembers.length > 0 ? (
            <MembersList
              kitchen_capacity={kitchen_capacity}
              members={displayMembers}
              isRequests
              currentPage={currentPage}
              currentMembers={currentMembers}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              handlePageChange={handlePageChange}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              hasSearchFilter={hasSearchFilter}
            />
          ) : (
            <div className="flex flex-row justify-evenly font-open-sans font-light text-center text-sm text-[#013847]/40 ">
              No member requests to show
            </div>
          )
        }
      </div>
      
    </div>
    </div>
  )
}



export const AllResourceRequests: React.FC<{
  resourceRequests: ResourceBookingSchema[]
  resourceType: ResourceType
  embedded?: boolean
}> = ({ resourceRequests, resourceType, embedded = false }) => {
  const [searchTerm, changeSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<ResourceRequestSortField>('created')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const itemsPerPage = 10

  const allRequests = resourceRequests ?? []

  const filteredRequests = useMemo(
    () => filterResourceRequests(resourceRequests ?? [], searchTerm),
    [resourceRequests, searchTerm]
  )

  const displayRequests = useMemo(
    () => sortResourceRequests(filteredRequests, sortField, sortDirection),
    [filteredRequests, sortField, sortDirection]
  )

  const totalPages = Math.ceil(displayRequests.length / itemsPerPage)
  const startIndex = displayRequests.length > 0 ? (currentPage - 1) * itemsPerPage : 0
  const endIndex = Math.min(startIndex + itemsPerPage, displayRequests.length)
  const currentRequests = displayRequests.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleSort = useCallback((field: ResourceRequestSortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSortField(field)
    setSortDirection(field === 'created' || field === 'start_time' ? 'desc' : 'asc')
  }, [sortField])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, sortField, sortDirection])

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  useEffect(() => {
    document.title = `HomeCook | ${capitalizeString(resourceType)} Requests`;
  }, [resourceType, resourceRequests]);

  const hasSearchFilter = searchTerm.trim().length > 0

  if (!embedded && resourceType === 'funding') {
    return <FundingPipelinePanel resourceType={resourceType} resourceRequests={resourceRequests} />;
  }

  const tableShellClass = embedded
    ? 'flex flex-col w-full bg-white rounded-xl border border-[#E4EEF1]'
    : 'flex flex-col w-11/12 bg-white mx-auto my-6 rounded-xl shadow-md';

  return (
    <>
    <div className={tableShellClass}>
      <div className="flex flex-row w-full justify-between items-center p-6 border-b border-gray-200">
      <p className="text-sm font-medium text-gray-500 font-open-sans" >
          {allRequests.length > 0 ? (
            displayRequests.length > 0
              ? `Showing ${startIndex + 1} - ${endIndex} of ${displayRequests.length} ${capitalizeString(resourceType)} Requests`
              : `Showing 0 - 0 of 0 ${capitalizeString(resourceType)} Requests`
          ) : (
            `Showing 0 - 0 of 0 ${capitalizeString(resourceType)} Requests`
          )}
        </p>
       

        <div className="flex ml-auto w-1/4">
          <NewSearch searchType="member name" searchTerm={searchTerm} changeSearchTerm={changeSearchTerm} />
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        {
          allRequests.length > 0 ? (
            <ResourceRequestList
              resourceType={resourceType}
              requests={displayRequests}
              currentPage={currentPage}
              currentRequests={currentRequests}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              handlePageChange={handlePageChange}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              hasSearchFilter={hasSearchFilter}
            />
          ) : (
            <div className="flex font-open-sans flex-row justify-center items-center h-32 text-sm text-gray-500">
              No {resourceType} request to show
            </div>
          )
        }
      </div>
    </div>
    </>
  )
}


type FundingPipelinePanelProps = {
  resourceType: ResourceType
  resourceRequests: ResourceBookingSchema[]
  resourceId?: string
  partnerSlug?: string
};

  /** One funding pipeline for community funding bookings. */
export const FundingPipelinePanel: FC<FundingPipelinePanelProps> = ({
  resourceType,
  resourceRequests,
  resourceId,
  partnerSlug,
}) => {
  if (resourceType !== 'funding') {
    return <AllResourceRequests resourceType={resourceType} resourceRequests={resourceRequests} />;
  }

  return (
    <div className="w-11/12 mx-auto my-6 page-card overflow-hidden" data-testid="funding-pipeline-panel">
      <div className="border-b border-[#E4EEF1] px-6 py-5">
        <p className="page-kicker">Funding pipeline</p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-raleway text-[20px] font-semibold text-ink">Partner referrals</h3>
            <p className="mt-1 text-sm font-open-sans text-muted">
              Consented partner introductions and HomeCook funding requests in one operational list.
            </p>
          </div>
          <FundingPipelineCampaign resourceId={resourceId} partnerSlug={partnerSlug} />
        </div>
      </div>
      <div className="px-2 pb-2 pt-4 sm:px-4">
        <FundingReferralInbox
          resourceType={resourceType}
          resourceRequests={resourceRequests}
          resourceId={resourceId}
          partnerSlug={partnerSlug}
        />
      </div>
    </div>
  );
};

// export default AllMemberRequests;
